import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { UserResource } from '../models/resource/user.resource';
import { User } from '../models/ui/user.model';
import { CaseSdk } from '../sdks/case.sdk';
import { UserSdk } from '../sdks/user.sdk';
import { ExportSdk } from '../sdks/export.sdk';

@Injectable({
	providedIn: 'root'
})
export class UserManager {
	public userCollection$: Observable<User[]>;

	private userCollectionSource: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);

	constructor(private userSdk: UserSdk, private caseSdk: CaseSdk, private exportSdk: ExportSdk) {
		this.userCollection$ = this.userCollectionSource.asObservable();
	}

	public refreshAdminUsers(term: string, orderBy: [string, "asc"| "desc"]): void {
		this.userSdk.getActiveUsers(orderBy, term)
			.subscribe(data =>
				this.userCollectionSource.next(data)
			);
	}

	public getUserDetails(userId: string): Observable<User> {
		return this.userSdk.getUserDetails(userId);
	}

	public getUploaders(): Observable<UserResource[]> {
		return this.getUsersByUserType('Uploader');
	}

	public getReviewers(): Observable<UserResource[]> {
		return this.getUsersByUserType('Reviewer');
	}

	public getSearchReportCreatedBy(): Observable<UserResource[]> {
		return this.getUsersByUserType('SearchReportCreatedBy');
	}

	public getAutoRedactionCreatedBy(): Observable<UserResource[]> {
		return this.getUsersByUserType('AutoRedactionCreatedBy');
	}

	public getProductionJobUsers(): Observable<UserResource[]> {
		return this.getUsersByUserType('ProductionJobUsers');
	}

	public getUsersByUserType(userType: string): Observable<UserResource[]> {
		return this.userSdk.getUsersByUserType(userType);
	}

	public getActiveUsers(term: string, orderBy: [string, "asc" | "desc"]): Observable<User[]> {
		return this.userSdk.getActiveUsers(orderBy, term);
	}

	public getEmployeeUsers(): Observable<User[]> {
		return this.userSdk.getEmployeeUsers();
	}

	public getUserCurrentCase(userId: string): Observable<string> {
		return this.userSdk.getUserCurrentCase(userId);
	}

	public getUserCasesUploadedDoc(userId: string, accountId: string): Observable<string[]> {
		return this.caseSdk.getUserCasesUploadedDoc(userId, accountId);
	}

	public updateUser(userId: string, deltaUser: Partial<UserResource>): Observable<void> {
		return this.userSdk.patch(userId, deltaUser);
	}

	public deleteUser(userId: string): Observable<void> {
		return this.userSdk.delete(userId);
	}

	public exportUsers(userIds: string[]): Observable<any> {
		return this.exportSdk.exportUsers(userIds).pipe(switchMap((url) => {
			window.open(url, '_blank');
			return of(true);
		}));
	}
}
